// src/components/opportunities/dealFailure.ts
//
// One vocabulary for deal mutations that fail, shared by the Pipeline board
// and the deal detail modal so the two cannot drift into different
// half-messages.

/** Why deals of an archived pipeline cannot be added or moved. One sentence, used everywhere. */
export const ARCHIVED_DEALS_REASON =
  // Truthful about what archiving does: deals are kept and can still be
  // edited or deleted (the server allows it); only adding deals and moving
  // them between stages stop.
  "This pipeline is archived. Deals are kept; new deals can't be added and deals can't move between stages until it is restored.";

/**
 * Turn a rejected deal mutation into something the user can act on.
 *
 * `verb` names the attempt ("create", "move", "delete", "update") so the
 * sentence says what did not happen rather than that something went wrong.
 */
export function describeDealFailure(error: unknown, verb: string): string {
  const status = (error as { status?: number } | undefined)?.status;
  const code = (error as { data?: { code?: string } } | undefined)?.data?.code;
  if (status === 401) return 'Your session has expired. Reload the page and sign in again.';
  // Archived between render and click (another tab, another admin).
  if (code === 'PIPELINE_ARCHIVED') return ARCHIVED_DEALS_REASON;
  if (status === 403 || code === 'CAPABILITY_DENIED') {
    return `You do not have permission to ${verb} deals in this workspace.`;
  }
  // 404 here is RECORD SCOPE, not a missing row: opportunitiesController
  // answers 404 when `canSeeDeal` denies the target, deliberately, so an id
  // cannot be used to confirm a record exists. "Not found" would read as a
  // bug; this says what it means without leaking whether the deal is real.
  if (status === 404) return `That deal is no longer available to you, so it could not be ${verb}d.`;
  if (status === 400) return `That deal could not be ${verb}d — check the details and try again.`;
  return `Could not ${verb} the deal. Please try again.`;
}
